/**
 * TagsInput Component
 * Provides tag management with autocomplete suggestions
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { X, Plus, Tag, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useContentTags } from '@/lib/hooks/use-content-hub'

// ============================================
// Types
// ============================================

interface TagsInputProps {
  value: string[]
  onChange: (tags: string[]) => void
  disabled?: boolean
  placeholder?: string
  maxTags?: number
  className?: string
  showSuggestions?: boolean
}

// ============================================
// TagsInput Component
// ============================================

export function TagsInput({
  value,
  onChange,
  disabled = false,
  placeholder = 'Add a tag...',
  maxTags,
  className,
  showSuggestions = true,
}: TagsInputProps) {
  const [inputValue, setInputValue] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Fetch existing tags for suggestions
  const { data: existingTags, isLoading: tagsLoading } = useContentTags({
    enabled: showSuggestions,
  })

  // Filter suggestions based on input
  const filteredSuggestions = existingTags
    ?.filter(
      (tag) =>
        tag.name.toLowerCase().includes(inputValue.toLowerCase()) &&
        !value.includes(tag.name)
    )
    .slice(0, 8) ?? []

  // Handle adding a tag
  const addTag = useCallback(
    (tag: string) => {
      const normalizedTag = tag.trim().toLowerCase()
      if (!normalizedTag) return
      if (value.includes(normalizedTag)) return
      if (maxTags && value.length >= maxTags) return

      onChange([...value, normalizedTag])
      setInputValue('')
      setShowDropdown(false)
      setHighlightedIndex(-1)
      inputRef.current?.focus()
    },
    [value, onChange, maxTags]
  )

  // Handle removing a tag
  const removeTag = useCallback(
    (tagToRemove: string) => {
      onChange(value.filter((tag) => tag !== tagToRemove))
    },
    [value, onChange]
  )

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        if (highlightedIndex >= 0 && filteredSuggestions[highlightedIndex]) {
          addTag(filteredSuggestions[highlightedIndex].name)
        } else if (inputValue.trim()) {
          addTag(inputValue)
        }
      } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
        const lastTag = value[value.length - 1]
        if (lastTag) {
          removeTag(lastTag)
        }
      } else if (e.key === 'Escape') {
        setShowDropdown(false)
        setHighlightedIndex(-1)
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightedIndex((prev) =>
          prev < filteredSuggestions.length - 1 ? prev + 1 : prev
        )
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1))
      } else if (e.key === ',') {
        e.preventDefault()
        if (inputValue.trim()) {
          addTag(inputValue)
        }
      }
    },
    [inputValue, value, highlightedIndex, filteredSuggestions, addTag, removeTag]
  )

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false)
        setHighlightedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Show dropdown when input has value and suggestions exist
  useEffect(() => {
    if (inputValue.length >= 1 && filteredSuggestions.length > 0 && showSuggestions) {
      setShowDropdown(true)
    } else {
      setShowDropdown(false)
    }
    setHighlightedIndex(-1)
  }, [inputValue, filteredSuggestions.length, showSuggestions])

  const canAddMore = !maxTags || value.length < maxTags

  return (
    <div className={cn('space-y-3', className)}>
      {/* Input Row */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (inputValue.length >= 1 && filteredSuggestions.length > 0 && showSuggestions) {
                setShowDropdown(true)
              }
            }}
            placeholder={canAddMore ? placeholder : 'Maximum tags reached'}
            disabled={disabled || !canAddMore}
            className="rounded-xl"
            aria-label="Add tag"
            aria-autocomplete="list"
            aria-controls="tag-suggestions"
            aria-expanded={showDropdown}
          />

          {/* Suggestions Dropdown */}
          {showDropdown && (
            <div
              ref={dropdownRef}
              id="tag-suggestions"
              role="listbox"
              className="absolute z-50 top-full left-0 right-0 mt-1 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-lg overflow-hidden"
            >
              {tagsLoading ? (
                <div className="flex items-center justify-center py-3">
                  <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />
                </div>
              ) : filteredSuggestions.length > 0 ? (
                <ul className="py-1 max-h-48 overflow-y-auto">
                  {filteredSuggestions.map((tag, index) => (
                    <li
                      key={tag.name}
                      role="option"
                      aria-selected={highlightedIndex === index}
                      className={cn(
                        'px-3 py-2 cursor-pointer flex items-center justify-between text-sm transition-colors',
                        highlightedIndex === index
                          ? 'bg-neutral-100 dark:bg-neutral-800'
                          : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/50'
                      )}
                      onClick={() => addTag(tag.name)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                    >
                      <span className="flex items-center gap-2">
                        <Tag className="h-3.5 w-3.5 text-neutral-400" />
                        <span className="text-neutral-900 dark:text-white">{tag.name}</span>
                      </span>
                      <span className="text-xs text-neutral-500 dark:text-neutral-400">
                        {tag.usageCount} use{tag.usageCount !== 1 ? 's' : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}

              {/* Create new tag option */}
              {inputValue.trim() && !filteredSuggestions.some((t) => t.name === inputValue.toLowerCase()) && (
                <div
                  className={cn(
                    'px-3 py-2 cursor-pointer flex items-center gap-2 text-sm border-t border-neutral-100 dark:border-neutral-800 transition-colors',
                    highlightedIndex === filteredSuggestions.length
                      ? 'bg-neutral-100 dark:bg-neutral-800'
                      : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/50'
                  )}
                  onClick={() => addTag(inputValue)}
                  onMouseEnter={() => setHighlightedIndex(filteredSuggestions.length)}
                >
                  <Plus className="h-3.5 w-3.5 text-blue-500" />
                  <span className="text-blue-600 dark:text-blue-400">
                    Create "{inputValue.trim()}"
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={() => {
            if (inputValue.trim()) {
              addTag(inputValue)
            }
          }}
          disabled={disabled || !inputValue.trim() || !canAddMore}
          className="rounded-xl"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {/* Tags Display */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              className="gap-1.5 py-1 px-2.5 text-sm rounded-lg"
            >
              <Tag className="h-3 w-3" />
              {tag}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="ml-0.5 hover:text-red-500 dark:hover:text-red-400 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 rounded-full"
                  aria-label={`Remove tag ${tag}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}

      {/* Helper Text */}
      {maxTags && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          {value.length} of {maxTags} tags
          {value.length >= maxTags && ' (maximum reached)'}
        </p>
      )}
    </div>
  )
}

export default TagsInput
